import { GridModel, isFormula } from "@omegagrid/grid";
import { PlotBand, PlotBandDefaults, PlotBandLabel, ReportContext } from "@reporting/rp-types/analytics";
import { ReportResult } from "../../model";
import { getGridSource } from "./tableAdapter";

/** chart view option axes holding a plot band list */
export type ChartAxisKey = 'x_axis'|'y_axis';

export const CHART_AXES: ChartAxisKey[] = ['y_axis', 'x_axis'];

/** highcharts options axis the view options axis maps to */
export const HIGHCHARTS_AXES: Record<ChartAxisKey, 'yAxis'|'xAxis'> = {
	y_axis: 'yAxis',
	x_axis: 'xAxis',
};

/** evaluated plot band, bounds resolved to numbers */
export type EvaluatedPlotBand = {
	from: number;
	to: number;
	color?: string;
	label?: PlotBandLabel;
}

/** highcharts label options of a band, undefined without a text */
export const getPlotBandLabel = (label: PlotBandLabel, style?: Record<string, string>) =>
	label?.text ? {
		text: label.text,
		align: label.align,
		verticalAlign: label.vertical_align,
		rotation: label.rotation,
		style,
	} : undefined;

/**
 * Grid model of the report data. View options may reference report cells
 * (=A1) the same way the editor grid does, the model evaluates them.
 */
export const createGridModel = (result: ReportResult, reportContext: ReportContext): GridModel => {
	if (!result || !reportContext?.context) return null;
	return new GridModel(getGridSource(result, reportContext));
}

/**
 * Resolve a view option value entered by the user - a plain value or a cell
 * reference expression (=A1). The model is passed as a factory, it is only
 * built when an expression is actually present.
 */
export const evaluateExpression = (value: string|number, gridModel: () => GridModel): string|number => {
	if (typeof value !== 'string' || !isFormula(value)) return value;
	return gridModel()?.calculations.evaluateExpression(value) as string|number;
}

/**
 * User defined plot bands with evaluated from/to expressions, incomplete ones
 * dropped. An unset "from" continues where the previous band ended (its
 * evaluated "to"), the first band starts at zero. The defaults are inherited
 * by every band without an own value (per label property).
 */
export const getPlotBands = (bands: PlotBand[], defaults: PlotBandDefaults, gridModel: () => GridModel): EvaluatedPlotBand[] => {
	let previousTo = 0;
	return (bands ?? [])
		.map(band => {
			const from = band.from == null || band.from === ''
				? previousTo
				: Number(evaluateExpression(band.from, gridModel));
			const to = Number(evaluateExpression(band.to, gridModel));
			if (isFinite(to)) previousTo = to;

			const label = defaults?.label || band.label
				? {...defaults?.label, ...band.label}
				: undefined;

			return {from, to, color: band.color || defaults?.color, label};
		})
		.filter(band => isFinite(band.from) && isFinite(band.to));
};

export const formatColor = (color: string) => /^[0-9A-Fa-f]{6}$/.test(color) ? `#${color}` : color;
